import { AppGlueError } from "../../AppGlueError";

export enum DataStoreQueryConditionType {
    Equals = "equals",
    EQUALS = "equals",
    Contains = "contains",
    CONTAINS = "contains",
    StartsWith = "startsWith",
    STARTS_WITH = "startsWith",
    DoesNotEqual = "doesNotEqual",
    DOES_NOT_EQUAL = "doesNotEqual",
    GreaterThan = "greaterThan",
    GREATER_THAN = "greaterThan",
    LessThan = "lessThan",
    LESS_THAN = "lessThan",
    GreaterThanEqual = "greaterThanEqual",
    GREATER_THAN_EQUAL = "greaterThanEqual",
    LessThanEqual = "lessThanEqual",
    LESS_THAN_EQUAL = "lessThanEqual",
    Exists = "exists",
    EXISTS = "exists",
    InArray = "inArray",
    IN_ARRAY = "inArray",
}

export class DataStoreQueryCondition {
    type: DataStoreQueryConditionType;
    fieldName: string;
    value: any;

    constructor(
        type: DataStoreQueryConditionType,
        fieldName: string,
        value: string | number | boolean | Date | string[],
    ) {
        this.type = type;
        this.fieldName = fieldName;
        this.value = value;
    }

    static validateCondition(condition: DataStoreQueryCondition) {
        switch (condition.type) {
            case DataStoreQueryConditionType.Contains:
                if (typeof condition.value !== "string")
                    throw new AppGlueError(
                        "Cannot evaluate contains than on field " + condition.fieldName + " because it is not a string",
                    );
                break;
            case DataStoreQueryConditionType.StartsWith:
                if (typeof condition.value !== "string")
                    throw new AppGlueError(
                        "Cannot starts with than on field " + condition.fieldName + " because it is not a string",
                    );
                break;
            case DataStoreQueryConditionType.GreaterThan:
                if (typeof condition.value !== "number" && !(condition.value instanceof Date))
                    throw new AppGlueError(
                        "Cannot evaluate greater than on field " +
                            condition.fieldName +
                            " because it is not a number or a date",
                    );
                break;
            case DataStoreQueryConditionType.LessThan:
                if (typeof condition.value !== "number" && !(condition.value instanceof Date))
                    throw new AppGlueError(
                        "Cannot evaluate less than on field " +
                            condition.fieldName +
                            " because it is not a number or a date",
                    );
                break;
            case DataStoreQueryConditionType.GreaterThanEqual:
                if (typeof condition.value !== "number" && !(condition.value instanceof Date))
                    throw new AppGlueError(
                        "Cannot evaluate greater than or equal to on field " +
                            condition.fieldName +
                            " because it is not a number or a date",
                    );
                break;
            case DataStoreQueryConditionType.LessThanEqual:
                if (typeof condition.value !== "number" && !(condition.value instanceof Date))
                    throw new AppGlueError(
                        "Cannot evaluate less or equal to on field " +
                            condition.fieldName +
                            " because it is not a number or a date",
                    );
                break;
        }
    }

    // used for memory based evaluation
    static evaluate(condition: DataStoreQueryCondition, v: object): boolean {
        this.validateCondition(condition);
        let value = Reflect.get(v, condition.fieldName);

        let conditionValue = condition.value;

        if (condition.value instanceof Date) {
            // handle dates differently
            if (Array.isArray(value)) {
                value = value.map((vv) => {
                    if (!(vv instanceof Date)) throw new AppGlueError("Value is not a date");
                    return (vv as Date).getTime();
                });
            } else {
                value = (value as Date).getTime();
            }
            conditionValue = (condition.value as Date).getTime();
        }

        switch (condition.type) {
            case DataStoreQueryConditionType.Equals:
                return value === conditionValue;
            case DataStoreQueryConditionType.Contains:
                if (!value) return false;
                return value.toString().toLowerCase().indexOf(conditionValue.toString().toLowerCase()) !== -1;
            case DataStoreQueryConditionType.StartsWith:
                if (!value) return false;

                return value.toString().toLowerCase().startsWith(conditionValue.toString().toLowerCase());
            case DataStoreQueryConditionType.DoesNotEqual:
                return value !== conditionValue;
            case DataStoreQueryConditionType.GreaterThan:
                if (!value) return false;

                return value > conditionValue;
            case DataStoreQueryConditionType.InArray:
                if (!value) return false;
                if (!Array.isArray(conditionValue)) {
                    throw new AppGlueError("must provide a list to use equals_one_of condition");
                }

                return conditionValue.findIndex((condition_value) => condition_value === value) > -1;
            case DataStoreQueryConditionType.LessThan:
                if (!value) return false;
                return value < conditionValue;
            case DataStoreQueryConditionType.Exists:
                return value !== undefined && value !== "" && value !== null;
            case DataStoreQueryConditionType.GreaterThanEqual:
                if (!value) return false;
                return value >= conditionValue;
            case DataStoreQueryConditionType.LessThanEqual:
                if (!value) return false;
                return value <= conditionValue;
        }
    }

    toString(): string {
        return this.fieldName + " " + this.type + " " + this.value;
    }
}
